import React, { useState, useEffect } from 'react';
import { storage, firestore } from '../../firebase-config';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';

const PhotoUpload = () => {
  const [photo, setPhoto] = useState(null);
  const [photoName, setPhotoName] = useState('');
  const [photoDate, setPhotoDate] = useState('');
  const [uploadURL, setUploadURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [gallery, setGallery] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    const querySnapshot = await getDocs(collection(firestore, 'gallery'));
    const items = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setGallery(items);
  };

  const handleImageSelection = (file) => {
    if (!file) return;
    
    // Check file extension
    const validExtensions = ['.jpg', '.jpeg', '.png', '.heic'];
    const extension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validExtensions.includes(extension)) {
      alert('Please select a valid image file (jpg, jpeg, png, or heic)');
      return;
    }

    setPhoto(file);
    setUploading(true);

    const storageRef = ref(storage, `gallery/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      null,
      (error) => {
        console.error('Upload error:', error);
        setUploading(false);
      },
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        setUploadURL(url);
        setUploading(false);
      }
    );
  };

  const handleSaveDetails = async () => {
    if (!photoName || (!uploadURL && !editingId)) {
      alert('Please enter a photo name and upload a photo.');
      return;
    }

    const payload = {
      name: photoName,
    };
    if (photoDate) {
      payload.date = photoDate;
    }
    if (uploadURL) payload.imageUrl = uploadURL;

    if (editingId) {
      await updateDoc(doc(firestore, 'gallery', editingId), payload);
      alert('Photo updated!');
    } else {
      await addDoc(collection(firestore, 'gallery'), payload);
      alert('Photo saved!');
    }

    resetForm();
    fetchGallery();
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setPhotoName(item.name);
    setPhotoDate(item.date);
    setUploadURL(item.imageUrl);
    setPhoto(null);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      const imagePath = decodeURIComponent(new URL(item.imageUrl).pathname.split('/o/')[1]).split('?')[0];
      const fileRef = ref(storage, imagePath);
      await deleteObject(fileRef).catch(() => console.warn('Storage deletion skipped'));

      await deleteDoc(doc(firestore, 'gallery', item.id));
      alert('Deleted!');
      fetchGallery();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const resetForm = () => {
    setPhoto(null);
    setPhotoName('');
    setPhotoDate('');
    setUploadURL(null);
    setEditingId(null);
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: 'auto' }}>
      <h2>{editingId ? 'Edit Photo' : 'Upload Photo'}</h2>

      <input
        type="file"
        accept=".jpg,.jpeg,.png,.heic"
        onChange={(e) => handleImageSelection(e.target.files[0])}
        style={{ marginBottom: '10px' }}
      />
      {uploading && <p>Uploading image...</p>}
      {uploadURL && (
        <img
          src={uploadURL}
          alt="preview"
          style={{ width: '100%', marginTop: '10px', maxHeight: '300px', objectFit: 'cover' }}
        />
      )}

      <input
        type="text"
        placeholder="Photo Name"
        value={photoName}
        onChange={(e) => setPhotoName(e.target.value)}
        style={{ display: 'block', margin: '10px 0', width: '100%', padding: '8px' }}
      />
      <input
        type="date"
        value={photoDate}
        onChange={(e) => setPhotoDate(e.target.value)}
        style={{ display: 'block', marginBottom: '10px', width: '100%', padding: '8px' }}
        placeholder="Date (Optional)"
      />
      <button onClick={handleSaveDetails} disabled={uploading} style={{ padding: '10px 20px' }}>
        {editingId ? 'Update' : 'Save'}
      </button>

      <hr style={{ margin: '30px 0' }} />
      <h3>Gallery</h3>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '15px',
        }}
      >
        {gallery.map((item) => (
          <div
            key={item.id}
            style={{
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '8px',
              textAlign: 'center',
              backgroundColor: '#f9f9f9',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <img
              src={item.imageUrl}
              alt={item.name}
              style={{
                width: '100%',
                height: '100px',
                objectFit: 'cover',
                borderRadius: '4px',
                marginBottom: '8px',
              }}
            />
            <h4
              style={{
                fontSize: '13px',
                margin: '5px 0',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                minHeight: '2.6em',
              }}
            >
              {item.name}
            </h4>
            <p style={{ fontSize: '11px', marginBottom: '10px' }}>{item.date || 'No date provided'}</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
              <button
                onClick={() => handleEdit(item)}
                style={{
                  fontSize: '12px',
                  padding: '5px 10px',
                  backgroundColor: '#ddd',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Edit
              </button>
              <button
                onClick={() => handleDelete(item)}
                style={{
                  fontSize: '12px',
                  padding: '5px 10px',
                  backgroundColor: 'red',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PhotoUpload;

